import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import {
  parseRautAssetDrawingBundlePath,
  parseRautAssetDrawingChunkIndex,
} from 'pages/reporting/reports/raut/utils/assetDrawingBundleStorage'
import { useCallback, useMemo } from 'react'

interface AssetDrawingUrlResult {
  path?: string
  signed_url?: string
}

export interface AssetDrawingBundle {
  bundlePath: string
  chunkKey: string
  descriptorHash: string
  descriptorPath?: string
  imagePath: string
  index: number
  inspectionId: string
  legendPath?: string
  legendSignedUrl?: string
  scanPlanPath?: string
  scanPlanSignedUrl?: string
  signedUrl?: string
  type: 'bundle'
}

export interface AssetDrawings {
  chunks: AssetDrawingBundle[]
}

function createEmptyBundle(
  parsedPath: NonNullable<ReturnType<typeof parseRautAssetDrawingBundlePath>>
): AssetDrawingBundle {
  return {
    bundlePath: parsedPath.bundlePath,
    chunkKey: parsedPath.chunkKey,
    descriptorHash: parsedPath.descriptorHash,
    imagePath: '',
    index: parseRautAssetDrawingChunkIndex(parsedPath.chunkKey) ?? 0,
    inspectionId: parsedPath.inspectionId,
    signedUrl: undefined,
    type: 'bundle',
  }
}

function getBundlePatch(
  parsedPath: NonNullable<ReturnType<typeof parseRautAssetDrawingBundlePath>>,
  result: AssetDrawingUrlResult
): Partial<AssetDrawingBundle> {
  switch (parsedPath.fileName) {
    case 'heatmap.png':
      return {
        imagePath: result.path ?? '',
        signedUrl: result.signed_url,
      }
    case 'scan-plan.png':
      return {
        scanPlanPath: result.path,
        scanPlanSignedUrl: result.signed_url,
      }
    case 'legend.png':
      return {
        legendPath: result.path,
        legendSignedUrl: result.signed_url,
      }
    case 'descriptor.json':
      return {
        descriptorPath: result.path,
      }
  }
}

export const useAssetDrawings = (
  metadataEntryId: string,
  inspectionIds: string[]
) => {
  const { fileRefs: assetDrawingRefs, forceRefresh: forceRefreshFileRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-screenshot/rautAssetDrawings/**',
    })

  const { results: assetDrawingUrls, forceRefresh: forceRefreshSignedUrls } =
    useRolodexSignedURLs(
      assetDrawingRefs.map((ref) => ({
        entry_id: metadataEntryId,
        path: ref.path,
      }))
    )

  const assetDrawings = useMemo(() => {
    const drawingsByInspection = new Map<string, AssetDrawings>()
    const bundlesByPath = new Map<string, AssetDrawingBundle>()

    inspectionIds.forEach((inspectionId) => {
      drawingsByInspection.set(inspectionId, { chunks: [] })
    })

    assetDrawingUrls.forEach((result) => {
      const parsedPath = parseRautAssetDrawingBundlePath(result.path)
      if (!parsedPath) return

      const existingBundle =
        bundlesByPath.get(parsedPath.bundlePath) ??
        createEmptyBundle(parsedPath)

      bundlesByPath.set(parsedPath.bundlePath, {
        ...existingBundle,
        ...getBundlePatch(parsedPath, result),
      })
    })

    bundlesByPath.forEach((bundle) => {
      if (!bundle.imagePath) return

      const existing = drawingsByInspection.get(bundle.inspectionId) ?? {
        chunks: [],
      }

      existing.chunks.push(bundle)
      drawingsByInspection.set(bundle.inspectionId, existing)
    })

    drawingsByInspection.forEach((drawings) => {
      drawings.chunks.sort((left, right) => left.index - right.index)
    })

    return drawingsByInspection as ReadonlyMap<string, AssetDrawings>
  }, [assetDrawingUrls, inspectionIds])

  const forceRefresh = useCallback(async () => {
    await forceRefreshFileRefs()
    await forceRefreshSignedUrls()
  }, [forceRefreshFileRefs, forceRefreshSignedUrls])

  return {
    assetDrawings,
    forceRefresh,
  }
}
